import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Gift, Check, Loader2, AlertCircle, LogIn } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useCatalog } from "../context/CatalogContext";
import { Button } from "../components/ui/button";
import BookCover from "../components/BookCover";

/**
 * Claiming an audiobook bought somewhere else.
 *
 * The page is reachable two ways: from the emailed link, which carries the code
 * in the path, and from someone typing one in. Both land here, and the code is
 * previewed before any sign-in is asked for — being told to make an account
 * before knowing whether the code is even valid is what makes redemption flows
 * feel like a trap.
 */
export default function Redeem() {
  const { code: codeFromUrl } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshMe } = useAuth();
  const { refresh: refreshCatalog } = useCatalog();

  const [code, setCode] = useState(codeFromUrl || "");
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(Boolean(codeFromUrl));
  const [redeeming, setRedeeming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [error, setError] = useState("");

  const look = useCallback(async (raw) => {
    const c = String(raw || "").trim();
    if (!c) return;
    setChecking(true);
    setError("");
    setPreview(null);
    try {
      const res = await api.get(`/redemptions/${encodeURIComponent(c)}`);
      setPreview(res.data);
      if (res.data.status !== "pending") {
        setError(
          res.data.status === "redeemed"
            ? "This code has already been used."
            : res.data.status === "expired"
              ? "This code has expired."
              : "This code has been cancelled."
        );
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "We could not check that code. Try again.");
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (codeFromUrl) look(codeFromUrl);
  }, [codeFromUrl, look]);

  const redeem = async () => {
    setRedeeming(true);
    setError("");
    try {
      const res = await api.post("/me/redeem", { code });
      setClaimed(res.data.books);
      // The library and the ownership badges read from the user object, so it
      // has to be refetched before the listener is sent anywhere.
      await refreshMe?.();
      await refreshCatalog?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "We could not redeem that code.");
    } finally {
      setRedeeming(false);
    }
  };

  const canRedeem = preview?.status === "pending" && !claimed;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 animate-float-up min-h-[70vh]">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-11 h-11 rounded-xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center">
          <Gift className="w-5 h-5 text-brand-400" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            {claimed ? "It's yours" : "Redeem a code"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {claimed
              ? "Added to your library."
              : "Bought an audiobook from us elsewhere? Enter the code from your email."}
          </p>
        </div>
      </div>

      {!claimed && (
        <form
          onSubmit={(e) => { e.preventDefault(); look(code); }}
          className="flex flex-col sm:flex-row gap-3 mb-8"
        >
          <label htmlFor="redeem-code" className="sr-only">Redemption code</label>
          <input
            id="redeem-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="TRL-XXXX-XXXX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className="flex-1 h-12 px-4 rounded-xl bg-secondary border border-border text-base font-mono tracking-widest uppercase outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
          />
          <Button
            type="submit"
            disabled={!code.trim() || checking}
            className="h-12 px-7 rounded-xl bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check code"}
          </Button>
        </form>
      )}

      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 mb-8">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-200">{error}</p>
        </div>
      )}

      {(preview?.books?.length > 0 || claimed) && (
        <div className="rounded-2xl border border-border bg-card/50 overflow-hidden mb-8">
          <div className="px-5 py-3.5 border-b border-border">
            <h2 className="font-semibold text-sm">
              {claimed
                ? `${claimed.length === 1 ? "Audiobook" : "Audiobooks"} added to your library`
                : `This code unlocks ${preview.books.length === 1 ? "this audiobook" : `these ${preview.books.length} audiobooks`}`}
            </h2>
          </div>
          <div className="divide-y divide-border/60">
            {(claimed || preview.books).map((b) => (
              <div key={b.id} className="flex items-center gap-4 px-5 py-4">
                <BookCover book={b} className="w-12 h-[72px] rounded-md cover-shadow shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{b.title_en}</p>
                  <p className="text-sm text-muted-foreground truncate">{b.author}</p>
                </div>
                {claimed ? (
                  <Check className="w-5 h-5 text-emerald-400 shrink-0" />
                ) : (
                  <Link
                    to={`/book/${b.slug || b.id}`}
                    className="text-sm text-brand-400 hover:text-brand-300 shrink-0"
                  >
                    Details
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {claimed && (
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => navigate("/library")}
            className="h-12 px-7 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold"
          >
            Go to my library
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/book/${claimed[0].slug || claimed[0].id}`)}
            className="h-12 px-6 rounded-full border-white/25 bg-white/5 hover:bg-white/10"
          >
            Start listening
          </Button>
        </div>
      )}

      {canRedeem && !authLoading && (
        user ? (
          <Button
            onClick={redeem}
            disabled={redeeming}
            className="h-12 px-8 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold text-base"
          >
            {redeeming ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redeeming…</>
            ) : (
              <>Add to my library</>
            )}
          </Button>
        ) : (
          // The entitlement has to attach to an account, so the sign-in step is
          // unavoidable — but it comes after the code is confirmed, and it
          // returns here rather than dumping the buyer on the home page.
          <div className="rounded-2xl border border-border bg-secondary/40 p-5">
            <p className="text-sm text-muted-foreground mb-4">
              Sign in or create an account to add {preview.books.length === 1 ? "it" : "them"} to your
              library. Your code is held until you do.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate(`/login?next=/redeem/${encodeURIComponent(code.trim())}`)}
                className="h-11 px-6 rounded-full bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold"
              >
                <LogIn className="w-4 h-4 mr-2" /> Sign in
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/register?next=/redeem/${encodeURIComponent(code.trim())}`)}
                className="h-11 px-6 rounded-full border-white/25 bg-white/5 hover:bg-white/10"
              >
                Create an account
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
